// Mapa Leaflet del territorio: base CARTO según el tema (solo con llave) + capa
// opcional «Satélite 2019 / 2026» con los compuestos Sentinel-2 del propio análisis.
// Callback ref: el mapa nace cuando el <div> entra al DOM.

import { useCallback, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { assetUrl, loadOverlays } from './data';

/** 'mapa' = teselas CARTO si hay llave; si no, «Sin fondo» (solo datos). */
export type BaseLayer = 'mapa' | '2019' | '2026';

// Mapa base: CARTO con llave (VITE_CARTO_API_KEY, parámetro `key`), igual que
// gov-health/GovMap y Atención Ciudadana. Sin llave NO se piden teselas (CARTO
// responde «API KEY REQUIRED» y la política de OSM desalienta uso en producción):
// se pintan solo las capas de datos sobre fondo neutro, con aviso. El mapa base es
// solo fondo visual, nunca insumo del análisis.
export const CARTO_KEY = ((import.meta.env.VITE_CARTO_API_KEY as string | undefined) || '').trim();
const isDark = () => document.documentElement.getAttribute('data-theme') === 'dark';
function tileUrl(): string {
  return `https://{s}.basemaps.cartocdn.com/${isDark() ? 'dark_all' : 'light_all'}/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(CARTO_KEY)}`;
}
export const ATRIBUCION_DATOS =
  'Contiene datos modificados de Copernicus Sentinel 2019–2026 · Colonias: © colaboradores de OpenStreetMap (ODbL)';

// Centro de la zona metropolitana de San Luis Potosí (AOI del análisis).
export const SLP_CENTER: L.LatLngExpression = [22.145, -100.965];

export function useTerritoryMap(inicial: BaseLayer = CARTO_KEY ? 'mapa' : '2026') {
  const mapRef = useRef<L.Map | null>(null);
  const [map, setMap] = useState<L.Map | null>(null);
  const [base, setBase] = useState<BaseLayer>(inicial);
  const overlayRef = useRef<L.ImageOverlay | null>(null);
  const tileRef = useRef<L.TileLayer | null>(null);

  const setNode = useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      mapRef.current?.remove();
      mapRef.current = null;
      setMap(null);
      return;
    }
    if (mapRef.current) return;
    const m = L.map(node, { zoomControl: true, attributionControl: true, preferCanvas: true }).setView(SLP_CENTER, 12);
    tileRef.current = CARTO_KEY
      ? L.tileLayer(tileUrl(), { attribution: `© OpenStreetMap · © CARTO · ${ATRIBUCION_DATOS}`, maxZoom: 19, subdomains: 'abcd' }).addTo(m)
      : null;
    if (!CARTO_KEY) m.attributionControl.addAttribution(ATRIBUCION_DATOS);
    mapRef.current = m;
    setMap(m);
    setTimeout(() => m.invalidateSize(), 60);
  }, []);

  // Tema claro/oscuro → teselas CARTO light_all / dark_all.
  useEffect(() => {
    const obs = new MutationObserver(() => tileRef.current?.setUrl(tileUrl()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  // Capa «Satélite»: el compuesto Sentinel-2 del análisis (2019 o 2026), reproyectado.
  useEffect(() => {
    if (!map) return;
    let vivo = true;
    overlayRef.current?.remove();
    overlayRef.current = null;
    if (base === 'mapa') return;
    loadOverlays().then((o) => {
      if (!vivo) return;
      const cfg = o[base];
      overlayRef.current = L.imageOverlay(assetUrl(cfg.img), cfg.bounds, { opacity: 1, interactive: false, className: 'gt-s2' }).addTo(map);
      overlayRef.current.bringToBack();
    }).catch(() => setBase('mapa'));
    return () => { vivo = false; };
  }, [map, base]);

  // El contenedor cambia de tamaño (cajones, rotación del móvil): re-medir.
  useEffect(() => {
    if (!map) return;
    const el = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);

  return { map, setNode, base, setBase };
}

export const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
